import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { AfterimagePass } from 'three/addons/postprocessing/AfterimagePass.js';
import { VisorGame } from './VisorGame';
import { StudioAudio } from './StudioAudio';

/**
 * Burnwood Studios. One scroll-driven timeline (p: 0..1):
 *   corridor (first person) -> fall into space -> the astronaut and the split
 *   title -> a tesseract of glass cubes -> a glass wall shatters -> join the
 *   crew among orbiting relics -> a ring back to the realm.
 */

// Timeline beats (progress values).
const T = {
  floorBreak: 0.045,
  space: 0.1,
  hero: 0.11, // he hangs in front of you, arms out
  fallEnd: 0.2, // ...tips back and falls away into the deep, tiny and tumbling
  closeUp: 0.245, // the camera dives after him onto the visor: the little game plays
  pullBack: 0.3, // back out to the full body, he waves
  reveal: 0.35, // the studio name slides in on the left
  tesseractIn: 0.5,
  shatter: 0.82,
  join: 0.87,
  portal: 0.95,
};

// Procedural poses for the Quaternius rig, layered over its own Idle/Wave clips.
// Degrees of Euler XYZ on top of the bind pose (arms down). Calibrated:
// UpperArm +X raises sideways (both sides), UpperArm Y twists (L -, R + turns the elbow
// bend upward), LowerArm Z bends the elbow (L -, R +), UpperLeg -X forward,
// LowerLeg +X bends the knee, Torso +X leans forward, Head +X nods, Y turns.
type Pose = Record<string, [number, number, number]>;
const BONES = ['Torso', 'Chest', 'Neck', 'Head', 'UpperArmL', 'UpperArmR', 'LowerArmL', 'LowerArmR', 'UpperLegL', 'UpperLegR', 'LowerLegL', 'LowerLegR'];
// Arms out at shoulder height, forearms up, palms out; legs loose. The opening hold.
const HERO: Pose = {
  Torso: [-4, 0, 0], Head: [-6, 0, 0],
  UpperArmL: [48, -90, 0], UpperArmR: [48, 90, 0], LowerArmL: [0, 0, -58], LowerArmR: [0, 0, 58],
  UpperLegL: [-4, 0, 0], UpperLegR: [3, 0, 0], LowerLegL: [8, 0, 0], LowerLegR: [14, 0, 0],
};
// Falling away: loosely curled, arms lifted as if the air is thick.
const DRIFT: Pose = {
  Torso: [16, 0, 0], Chest: [6, 0, 0], Head: [12, 0, 0],
  UpperArmL: [105, -60, 0], UpperArmR: [115, 60, 0], LowerArmL: [0, 0, -38], LowerArmR: [0, 0, 32],
  UpperLegL: [-28, 0, 0], UpperLegR: [-14, 0, 0], LowerLegL: [52, 0, 0], LowerLegR: [38, 0, 0],
};
const SURGES = 12; // stepped pushes through the tesseract
const LATTICE_START = 30; // the lattice begins behind the camera
const LANE = SURGES * 6 + 190; // lattice length along -Z: everything you'll travel plus what you can see ahead
const CELL = 6;

// Camera shots: position and look-at offsets from his body (a = 0) or his visor (a = 1).
type CamKey = { p: number; pos: THREE.Vector3; look: THREE.Vector3; a: number; fov: number; abs?: number };
const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
const CAM_KEYS: CamKey[] = [
  { p: 0.05, pos: V(0, 0.32, -7.3), look: V(0, 0.2, -40), a: 0, fov: 44, abs: 1 }, // he comes out of the dark
  { p: T.hero - 0.035, pos: V(0, 0.4, -7.2), look: V(0, 0.3, -40), a: 0, fov: 42, abs: 1 },
  { p: T.hero, pos: V(0, 0.38, -7.3), look: V(0, 0.28, -40), a: 0, fov: 42, abs: 1 }, // big, arms out, cropped at the waist
  { p: T.fallEnd, pos: V(0, 0.3, -7.8), look: V(0, -0.6, -40), a: 0, fov: 40, abs: 1 }, // he falls away down the middle
  { p: T.closeUp + 0.01, pos: V(0, 0.06, 0.6), look: V(0, 0.02, 0), a: 1, fov: 40 }, // dive onto the visor
  { p: 0.29, pos: V(0, 0.04, 0.48), look: V(0, 0.02, 0), a: 1, fov: 38 }, // the game plays
  { p: 0.36, pos: V(-1.5, 0.12, 3.9), look: V(-1.5, 0.05, 0), a: 0, fov: 50 }, // full body, right of frame
  { p: 0.45, pos: V(-1.4, 0.25, 3.6), look: V(-1.45, 0.1, 0), a: 0, fov: 50 },
  { p: T.tesseractIn, pos: V(0, 0.3, 12), look: V(0, 0, -20), a: 0, fov: 54 }, // down the tunnel, he's small
  { p: T.shatter, pos: V(0, 0.3, 11), look: V(0, 0, -20), a: 0, fov: 54 },
  { p: T.join + 0.02, pos: V(0, 0.6, 6.5), look: V(0, 0.05, -4), a: 0, fov: 52 }, // in close for the finale
  { p: 1.01, pos: V(0, 0.6, 6.5), look: V(0, 0.05, -4), a: 0, fov: 52 },
];

export type StudioHooks = { onExit: () => void; roar: () => void; whoosh: () => void; shatter?: () => void };

type Shard = { mesh: THREE.Mesh; vel: THREE.Vector3; spin: THREE.Vector3 };

// Runs right after the scene render: any NaN/inf pixel (one bad shading sample) would
// otherwise be smeared across the whole screen by the bloom blur. Zero it and clamp.
const SanitizeShader = {
  uniforms: { tDiffuse: { value: null } },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: 'uniform sampler2D tDiffuse; varying vec2 vUv; void main(){ vec4 c = texture2D(tDiffuse, vUv); if (any(isnan(c)) || any(isinf(c))) c = vec4(0.0, 0.0, 0.0, 1.0); gl_FragColor = clamp(c, 0.0, 48.0); }',
};

// Final-image pass: chromatic halo ring, vignette, flash and grain.
const HaloShader = {
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uHalo: { value: 0.0 },
    uFlash: { value: 0.0 },
    uAspect: { value: 1.0 },
    uFade: { value: 0.0 },
  },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse; uniform float uTime, uHalo, uFlash, uAspect, uFade; varying vec2 vUv;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
    void main(){
      vec2 c = vUv - 0.5; c.x *= uAspect;
      float r = length(c);
      // Colour fringing grows toward the edges, strongest on a soft ring.
      float ring = smoothstep(0.32, 0.5, r) * (1.0 - smoothstep(0.5, 0.85, r));
      vec2 dir = normalize(c + 1e-5) * (0.0015 + 0.008 * ring * uHalo);
      dir.x /= uAspect;
      vec3 col;
      col.r = texture2D(tDiffuse, vUv + dir).r;
      col.g = texture2D(tDiffuse, vUv).g;
      col.b = texture2D(tDiffuse, vUv - dir).b;
      // A faint spectral halo: hue sweeps around the ring.
      float a = atan(c.y, c.x);
      vec3 spectrum = 0.5 + 0.5 * cos(6.2831 * (a / 6.2831 + vec3(0.0, 0.33, 0.67)) + uTime * 0.2);
      col += spectrum * ring * 0.06 * uHalo;
      col *= 1.0 - smoothstep(0.55, 1.1, r) * 0.55;
      col += (hash(vUv * 931.0 + uTime) - 0.5) * 0.035;
      col = mix(col, vec3(1.0), uFlash);
      col = mix(col, vec3(0.0), uFade);
      if (any(isnan(col)) || any(isinf(col))) col = vec3(0.0);
      gl_FragColor = vec4(clamp(col, 0.0, 64.0), 1.0);
    }`,
};

const ease = (x: number) => x * x * (3 - 2 * x);

// Smooth 1D value noise in [-1, 1]: organic, never visibly looping like a sine.
const hash1 = (n: number) => { const x = Math.sin(n * 127.1) * 43758.5453; return x - Math.floor(x); };
const vnoise = (x: number) => { const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f); return (hash1(i) * (1 - u) + hash1(i + 1) * u) * 2 - 1; };
const fbm = (x: number) => vnoise(x) * 0.62 + vnoise(x * 2.13 + 17.3) * 0.28 + vnoise(x * 4.37 + 41.1) * 0.1;

// Per-bone spring feel: stiffness and damping ratio. Distal joints are looser and lag more,
// so motion rolls down the limb (overlapping action) and overshoots before it settles.
const SPRING: Record<string, [number, number]> = {
  Torso: [60, 0.85], Chest: [55, 0.8], Neck: [45, 0.7], Head: [38, 0.6],
  UpperArmL: [26, 0.5], UpperArmR: [26, 0.5], LowerArmL: [14, 0.38], LowerArmR: [14, 0.38],
  UpperLegL: [24, 0.55], UpperLegR: [24, 0.55], LowerLegL: [13, 0.4], LowerLegR: [13, 0.4],
};
type Spring = { x: THREE.Vector3; v: THREE.Vector3 };
/** Semi-implicit damped spring step toward target, substepped for stability. */
function springTo(sp: Spring, target: THREE.Vector3, k: number, zeta: number, dt: number): void {
  const c = 2 * zeta * Math.sqrt(k);
  const n = Math.max(1, Math.ceil(dt / 0.008));
  const h = Math.min(dt, 0.1) / n;
  for (let i = 0; i < n; i++) {
    sp.v.x += (k * (target.x - sp.x.x) - c * sp.v.x) * h;
    sp.v.y += (k * (target.y - sp.x.y) - c * sp.v.y) * h;
    sp.v.z += (k * (target.z - sp.x.z) - c * sp.v.z) * h;
    sp.x.addScaledVector(sp.v, h);
  }
}

/** Soft rounded-rectangle mask so the visor screen fades into the gold glass. */
function visorAlpha(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 160;
  const g = c.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, 256, 160);
  g.filter = 'blur(10px)';
  g.fillStyle = '#fff';
  g.beginPath();
  g.roundRect(22, 20, 212, 120, 42);
  g.fill();
  return new THREE.CanvasTexture(c);
}
const band = (p: number, a: number, b: number) => THREE.MathUtils.clamp((p - a) / (b - a), 0, 1);

export class Studio {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(55, 1, 0.05, 900);
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private readonly halo: ShaderPass;
  private readonly root = document.querySelector<HTMLElement>('#studio')!;
  private readonly rail = document.querySelector<HTMLElement>('#studio-rail')!;
  // Scene parts.
  private readonly corridor = new THREE.Group();
  private readonly space = new THREE.Group();
  private readonly tesseract = new THREE.Group();
  private readonly finale = new THREE.Group();
  private corridorPieces: { obj: THREE.Object3D; vel: THREE.Vector3; spin: THREE.Vector3; mats: THREE.Material[] }[] = [];
  private torches: THREE.PointLight[] = [];
  private astronaut!: THREE.Object3D;
  private readonly astroLights = new THREE.Group();
  private mixer!: THREE.AnimationMixer;
  private springs: Record<string, Spring> = {};
  private readonly gaze: Spring = { x: new THREE.Vector3(), v: new THREE.Vector3() };
  private readonly audio = new StudioAudio();
  private visorLight!: THREE.PointLight;
  private ignitedHit = false;
  private joined = false;
  private portalGlow = 0;
  private joinPulse = 0;
  private lastP = 0;
  private gateOpen = false;
  private readonly rootSpring: Spring = { x: new THREE.Vector3(), v: new THREE.Vector3() };
  private rootInit = false;
  private readonly prevRoot = new THREE.Quaternion();
  private readonly spin = new THREE.Vector3(); // smoothed angular velocity, body space
  private idle!: THREE.AnimationAction;
  private wave!: THREE.AnimationAction;
  private bones: Record<string, { bone: THREE.Bone; rest: THREE.Quaternion }> = {};
  private head!: THREE.Bone;
  private readonly headLocal = new THREE.Vector3(); // visor centre in head-bone space
  private visorGame = new VisorGame();
  private visorScreen!: THREE.Mesh;
  private splitLines: HTMLElement[][] = [];
  private planet!: THREE.Mesh;
  private stars!: THREE.Points;
  private glassWall!: THREE.Group;
  private shards: Shard[] = [];
  private relics: THREE.Object3D[] = [];
  private portal!: THREE.Mesh;
  private dust!: THREE.Points;
  private beamMat!: THREE.MeshStandardMaterial;
  private glassMat!: THREE.ShaderMaterial;
  private fragments!: THREE.InstancedMesh;
  private fragBase!: Float32Array;
  private readonly afterimage = new AfterimagePass(0);
  private trailDamp = 0;
  private paletteStage = -1;
  private readonly astroBase = new THREE.Vector3(0, -0.2, -7);
  private readonly tunnelFog = new THREE.Fog(0x000000, 30, 120);
  private sections: { el: HTMLElement; a: number; b: number }[] = [];
  private revealLines: HTMLElement[] = [];
  private revealRule: HTMLElement | null = null;
  // State.
  private built = false;
  private active = false;
  private p = 0;
  private targetP = 0;
  private shattered = false;
  private fell = false;
  private look = new THREE.Vector2();
  private lookTarget = new THREE.Vector2();
  private touchY: number | null = null;
  private readonly keys = new Set<string>();
  private prevTone = { mapping: THREE.NoToneMapping as THREE.ToneMapping, exposure: 1 };

  constructor(private readonly renderer: THREE.WebGLRenderer, private readonly hooks: StudioHooks) {
    const rt = new THREE.WebGLRenderTarget(1, 1, { samples: 4, type: THREE.HalfFloatType });
    this.composer = new EffectComposer(renderer, rt);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.composer.addPass(new ShaderPass(SanitizeShader));
    // Ghost trails while the tunnel surges (off the rest of the time).
    this.afterimage.enabled = false;
    this.composer.addPass(this.afterimage);
    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.55, 0.45, 0.88);
    this.composer.addPass(this.bloom);
    this.halo = new ShaderPass(HaloShader);
    this.composer.addPass(this.halo);
    this.composer.addPass(new OutputPass());
    this.scene.background = new THREE.Color(0x000000);
    this.scene.add(this.corridor, this.space, this.tesseract, this.finale);
    this.bindInput();
  }

  get isActive(): boolean {
    return this.active;
  }

  // ------------------------------------------------------------------ build

  private async build(): Promise<void> {
    if (this.built) return;
    this.built = true;
    const pm = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture;
    const loader = new GLTFLoader();
    const base = import.meta.env.BASE_URL + 'models/';
    await this.buildCorridor(loader, base);
    await this.buildAstronaut(loader, base);
    this.buildSpace();
    this.buildTesseract();
    this.buildFinale(loader, base);
    this.buildHtml();
  }

  private async buildCorridor(loader: GLTFLoader, base: string): Promise<void> {
    const load = async (f: string) => (await loader.loadAsync(base + 'dungeon/gltf/' + f)).scene;
    const [wall, arched, floor, torch] = [await load('wall.gltf.glb'), await load('wall_arched.gltf.glb'), await load('floor_tile_large.gltf.glb'), await load('torch_mounted.gltf.glb')];
    const fit = (src: THREE.Object3D, h: number) => {
      const o = src.clone(true);
      const box = new THREE.Box3().setFromObject(o);
      const s = h / (box.max.y - box.min.y);
      o.scale.setScalar(s);
      o.position.y = -box.min.y * s;
      const g = new THREE.Group();
      g.add(o);
      g.userData.w = (box.max.x - box.min.x) * s;
      return g;
    };
    const add = (obj: THREE.Object3D) => {
      const mats: THREE.Material[] = [];
      obj.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.material = (m.material as THREE.Material).clone();
        mats.push(m.material as THREE.Material);
      });
      this.corridor.add(obj);
      this.corridorPieces.push({ obj, vel: new THREE.Vector3(), spin: new THREE.Vector3(), mats });
      obj.userData.home = obj.position.clone();
      obj.userData.rot = obj.rotation.clone();
    };
    const W = 2.2;
    const seg = fit(wall, 4.2).userData.w as number;
    for (let z = 2; z > -14; z -= seg) for (const side of [-1, 1]) {
      const piece = fit(Math.round(z / seg) % 3 === 0 ? arched : wall, 4.2);
      piece.position.set(side * W, 0, z);
      piece.rotation.y = side * (Math.PI / 2);
      add(piece);
    }
    const fw = fit(floor, 0.2).userData.w as number;
    for (let z = 2; z > -14; z -= fw) for (const x of [-fw / 2, fw / 2]) {
      const t = fit(floor, 0.2);
      t.position.set(x, -0.2, z);
      t.userData.floor = true;
      add(t);
    }
    for (let i = 0; i < 4; i++) {
      const side = i % 2 ? 1 : -1;
      const t = fit(torch, 0.9);
      t.position.set(side * (W - 0.25), 2.2, -1 - i * 3.4);
      t.rotation.y = side * (-Math.PI / 2);
      add(t);
      const l = new THREE.PointLight(0xff8a3a, 6, 9, 1.6);
      l.position.set(side * (W - 0.6), 2.7, -1 - i * 3.4);
      this.corridor.add(l);
      this.torches.push(l);
    }
    const glow = new THREE.PointLight(0xff5a1a, 26, 26, 1.4);
    glow.position.set(0, 1.6, -16);
    this.corridor.add(glow, new THREE.AmbientLight(0x3a2a24, 0.5));
    this.torches.push(glow);
  }

  private async buildAstronaut(loader: GLTFLoader, base: string): Promise<void> {
    const gltf = await loader.loadAsync(base + 'astronaut/astronaut.glb');
    const model = cloneSkinned(gltf.scene) as THREE.Object3D;
    // Re-skin: a white suit, graphite joints, a dark glossy visor, ember trim.
    const suit = new THREE.MeshPhysicalMaterial({ color: 0xf1eee9, roughness: 0.42, clearcoat: 0.5, clearcoatRoughness: 0.3 });
    const joints = new THREE.MeshStandardMaterial({ color: 0x2a2c31, roughness: 0.55, metalness: 0.3 });
    const visor = new THREE.MeshPhysicalMaterial({ color: 0x0b0d12, roughness: 0.06, metalness: 0.3, clearcoat: 1, clearcoatRoughness: 0.03 });
    const ember = new THREE.MeshStandardMaterial({ color: 0xff7a2e, emissive: 0xff5a14, emissiveIntensity: 0.45 });
    const visorMeshes: THREE.Mesh[] = [];
    model.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isMesh) return;
      m.frustumCulled = false;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const mapped = mats.map((mm) => {
        const n = (mm as THREE.Material).name;
        if (n === 'SciFi_Main' || n === 'SciFi_Light') return suit;
        if (n === 'SciFi_Light_Accent') return ember;
        if (m.name.includes('Head') && n === 'SciFi_MainDark') { visorMeshes.push(m); return visor; }
        return joints;
      });
      m.material = Array.isArray(m.material) ? mapped : mapped[0];
    });
    model.traverse((o) => {
      if ((o as THREE.Bone).isBone) this.bones[o.name] = { bone: o as THREE.Bone, rest: o.quaternion.clone() };
    });
    this.head = this.bones.Head.bone;
    // The rig carries a 100x armature scale and tiny raw meshes, so measure the
    // posed, skinned result (precise box) rather than the raw geometry.
    model.updateMatrixWorld(true);
    model.traverse((o) => (o as THREE.SkinnedMesh).skeleton?.update());
    const box = new THREE.Box3().setFromObject(model, true);
    const s = 1.8 / Math.max(1e-6, box.max.y - box.min.y);
    model.scale.setScalar(s);
    model.position.set(-((box.max.x + box.min.x) / 2) * s, -((box.max.y + box.min.y) / 2) * s, -((box.max.z + box.min.z) / 2) * s);
    const holder = new THREE.Group();
    holder.add(model);
    this.astronaut = holder;
    this.scene.add(holder);
    holder.updateMatrixWorld(true);
    model.traverse((o) => (o as THREE.SkinnedMesh).skeleton?.update());
    // His own clips do the idle float and the wave; the fall poses layer on top.
    this.mixer = new THREE.AnimationMixer(model);
    const clip = (name: string) => gltf.animations.find((a) => a.name.endsWith(name))!;
    this.idle = this.mixer.clipAction(clip('Idle_Neutral'));
    this.idle.timeScale = 0.35;
    this.idle.play();
    this.wave = this.mixer.clipAction(clip('Wave'));
    this.wave.timeScale = 0.6;
    this.wave.setEffectiveWeight(0);
    this.wave.play();

    // The visor screen: a curved pane on the front of the visor, riding the head bone.
    const vb = new THREE.Box3();
    for (const m of visorMeshes) vb.expandByObject(m, true);
    if (vb.isEmpty()) vb.setFromCenterAndSize(this.head.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0.05, 0.12)), new THREE.Vector3(0.2, 0.15, 0.1));
    const size = vb.getSize(new THREE.Vector3());
    const w = size.x * 0.9, h = Math.min(size.y * 0.8, w * 0.66);
    const geo = new THREE.PlaneGeometry(w, h, 24, 12);
    const pa = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pa.count; i++) {
      const x = pa.getX(i) / (w / 2), y = pa.getY(i) / (h / 2);
      pa.setZ(i, -(x * x * 0.32 + y * y * 0.12) * size.z);
    }
    this.visorScreen = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: new THREE.Color(1.5, 1.45, 1.4), map: this.visorGame.texture, alphaMap: visorAlpha(), toneMapped: false, transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }));
    this.visorScreen.position.set(vb.getCenter(new THREE.Vector3()).x, vb.min.y + size.y * 0.54, vb.max.z + 0.004);
    this.visorScreen.renderOrder = 3;
    this.scene.add(this.visorScreen);
    this.head.attach(this.visorScreen);
    // When the visor ignites, its light spills onto the helmet and face.
    this.visorLight = new THREE.PointLight(0xff7a2a, 0, 1.1, 2);
    this.visorLight.position.set(0, 0, 0.16);
    this.visorScreen.add(this.visorLight);
    this.headLocal.copy(this.visorScreen.position);

    // Lights travel with him but don't spin with him, so the tumble reads in the shading.
    const rim = new THREE.DirectionalLight(0x9fc8ff, 2.2);
    rim.position.set(-3, 2, -4);
    const key = new THREE.DirectionalLight(0xfff0e0, 2.6);
    key.position.set(4, 5, 4);
    const fill = new THREE.DirectionalLight(0xffb27a, 0.35);
    fill.position.set(0, -4, 3);
    this.astroLights.add(rim, key, fill, rim.target, key.target, fill.target);
    this.scene.add(this.astroLights);

  }

  /**
   * Blend weighted procedural poses plus live offsets (degrees), then lay the result over
   * whatever the clips just wrote, by `amount` (0 = clips only, 1 = procedural only).
   */
  private pose(weights: [Pose, number][], extra: Pose, amount: number, dt: number): void {
    const e = new THREE.Euler();
    const q = new THREE.Quaternion();
    const d = THREE.MathUtils.DEG2RAD;
    const total = weights.reduce((a, [, w]) => a + w, 0) || 1;
    const target = new THREE.Vector3();
    for (const name of BONES) {
      const b = this.bones[name];
      if (!b) continue;
      target.set(0, 0, 0);
      for (const [p, w] of weights) {
        const v = p[name];
        if (v) target.add(new THREE.Vector3(v[0], v[1], v[2]).multiplyScalar(w / total));
      }
      const ex = extra[name];
      if (ex) target.add(new THREE.Vector3(ex[0], ex[1], ex[2]));
      const sp = (this.springs[name] ??= { x: target.clone(), v: new THREE.Vector3() });
      const [k, z] = SPRING[name] ?? [30, 0.6];
      springTo(sp, target, k, z, dt);
      if (amount <= 0.001) continue;
      q.copy(b.rest).multiply(new THREE.Quaternion().setFromEuler(e.set(sp.x.x * d, sp.x.y * d, sp.x.z * d, 'XYZ')));
      b.bone.quaternion.slerp(q, Math.min(1, amount));
    }
  }

  private buildSpace(): void {
    // Stars: two shells, the near one twinkles.
    const N = 5000;
    const pos = new Float32Array(N * 3);
    const size = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      const v = new THREE.Vector3().randomDirection().multiplyScalar(220 + Math.random() * 380);
      pos.set([v.x, v.y, v.z], i * 3);
      size[i] = Math.random() < 0.06 ? 3.2 : 0.8 + Math.random() * 1.6;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('size', new THREE.BufferAttribute(size, 1));
    this.stars = new THREE.Points(g, new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 } },
      vertexShader: 'attribute float size; uniform float uTime; varying float vT; void main(){ vec4 mv = modelViewMatrix * vec4(position,1.0); vT = 0.65 + 0.35*sin(uTime*1.7 + position.x*0.13 + position.z*0.07); gl_PointSize = size * (300.0 / -mv.z) * 0.5 + 1.0; gl_Position = projectionMatrix * mv; }',
      fragmentShader: 'uniform float uOpacity; varying float vT; void main(){ float d = length(gl_PointCoord - 0.5); float a = 1.0 - smoothstep(0.0, 0.5, d); gl_FragColor = vec4(vec3(1.0, 0.96, 0.9) * 2.2, a * vT * uOpacity); }',
    }));
    this.stars.frustumCulled = false;
    this.space.add(this.stars);

    // Burnwood's little world far below: ocean-and-forest ball with an ember-warm atmosphere.
    const planetMat = new THREE.ShaderMaterial({
      uniforms: { uSun: { value: new THREE.Vector3(0.6, 0.5, 0.4).normalize() } },
      vertexShader: 'varying vec3 vN; varying vec3 vP; varying vec3 vV; void main(){ vN = normalize(normalMatrix*normal); vP = position; vec4 mv = modelViewMatrix*vec4(position,1.0); vV = normalize(-mv.xyz); gl_Position = projectionMatrix*mv; }',
      fragmentShader: /* glsl */ `
        uniform vec3 uSun; varying vec3 vN; varying vec3 vP; varying vec3 vV;
        float h(vec3 p){ return fract(sin(dot(p, vec3(12.9898,78.233,37.719)))*43758.5453); }
        float n3(vec3 x){ vec3 i=floor(x), f=fract(x); f=f*f*(3.0-2.0*f);
          return mix(mix(mix(h(i),h(i+vec3(1,0,0)),f.x),mix(h(i+vec3(0,1,0)),h(i+vec3(1,1,0)),f.x),f.y),
                     mix(mix(h(i+vec3(0,0,1)),h(i+vec3(1,0,1)),f.x),mix(h(i+vec3(0,1,1)),h(i+vec3(1,1,1)),f.x),f.y),f.z); }
        void main(){
          vec3 p = normalize(vP);
          float land = n3(p*3.0)*0.6 + n3(p*7.0)*0.3 + n3(p*15.0)*0.1;
          vec3 col = land > 0.52 ? mix(vec3(0.22,0.42,0.2), vec3(0.42,0.55,0.26), n3(p*20.0)) : vec3(0.08,0.24,0.38);
          float diff = max(dot(vN, uSun), 0.0);
          col *= 0.15 + diff*1.1;
          float rim = pow(1.0 - max(dot(vN, vV), 0.0), 3.0);
          col += vec3(1.0, 0.55, 0.25) * rim * 1.4;
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    this.planet = new THREE.Mesh(new THREE.SphereGeometry(70, 96, 64), planetMat);
    this.planet.position.set(38, -106, -170);
    this.space.add(this.planet);
    // Soft atmosphere halo around it.
    const atmo = new THREE.Mesh(new THREE.SphereGeometry(76, 64, 48), new THREE.ShaderMaterial({
      transparent: true, side: THREE.BackSide, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: 'varying vec3 vN; varying vec3 vV; void main(){ vN = normalize(normalMatrix*normal); vec4 mv = modelViewMatrix*vec4(position,1.0); vV = normalize(-mv.xyz); gl_Position = projectionMatrix*mv; }',
      fragmentShader: 'varying vec3 vN; varying vec3 vV; void main(){ float r = pow(1.0 - abs(dot(vN, vV)), 2.5); gl_FragColor = vec4(vec3(1.0,0.6,0.3)*r*1.2, r); }',
    }));
    this.planet.add(atmo);
    this.space.add(new THREE.HemisphereLight(0x8fb4ff, 0x1a0e08, 0.35));
  }

  private buildTesseract(): void {
    // A lattice tunnel: 5x5 cells around an empty centre lane, repeated down -Z. It starts
    // behind the camera so the whole frame is inside it.
    const cells: THREE.Vector3[] = [];
    for (let z = 0; z < LANE; z += CELL) {
      for (let gx = -2; gx <= 2; gx++) for (let gy = -2; gy <= 2; gy++) {
        if (gx === 0 && gy === 0) continue; // the lane
        if (Math.abs(gx) === 2 && Math.abs(gy) === 2) continue; // round off the corners
        cells.push(new THREE.Vector3(gx * CELL, gy * CELL, LATTICE_START - z));
      }
    }
    const dummy = new THREE.Object3D();
    const h = CELL / 2;
    // Every edge of every cube, as (centre, axis). Shared edges are drawn twice, which is fine.
    const edges: { c: THREE.Vector3; axis: 0 | 1 | 2 }[] = [];
    for (const c of cells) {
      for (const a of [-h, h]) for (const b of [-h, h]) {
        edges.push({ c: c.clone().add(new THREE.Vector3(a, b, 0)), axis: 2 });
        edges.push({ c: c.clone().add(new THREE.Vector3(a, 0, b)), axis: 1 });
        edges.push({ c: c.clone().add(new THREE.Vector3(0, a, b)), axis: 0 });
      }
    }
    const axisRot = [new THREE.Euler(0, Math.PI / 2, 0), new THREE.Euler(Math.PI / 2, 0, 0), new THREE.Euler(0, 0, 0)];

    // Dust first: tiny points strung along the edges, so the lattice draws itself in.
    const dustPos: number[] = [];
    for (const e of edges) {
      if (Math.random() < 0.55) continue;
      for (let i = 0; i < 7; i++) {
        const v = e.c.clone();
        v.setComponent(e.axis, v.getComponent(e.axis) + (Math.random() - 0.5) * CELL);
        dustPos.push(v.x, v.y, v.z);
      }
    }
    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.Float32BufferAttribute(dustPos, 3));
    this.dust = new THREE.Points(dg, new THREE.PointsMaterial({ color: 0xcfe4ff, size: 0.07, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    this.dust.frustumCulled = false;
    this.tesseract.add(this.dust);

    // Steel: thick dark beams on every edge.
    this.beamMat = new THREE.MeshStandardMaterial({ color: 0x3b3f47, metalness: 0.88, roughness: 0.38, transparent: true, opacity: 0 });
    const beams = new THREE.InstancedMesh(new THREE.BoxGeometry(0.2, 0.2, CELL), this.beamMat, edges.length);
    edges.forEach((e, i) => {
      dummy.position.copy(e.c);
      dummy.rotation.copy(axisRot[e.axis]);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      beams.setMatrixAt(i, dummy.matrix);
    });
    beams.frustumCulled = false;
    this.tesseract.add(beams);

    // Glass: faint fresnel panes in the cubes, just enough to catch light.
    this.glassMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 } },
      vertexShader: 'varying vec3 vN; varying vec3 vV; varying vec3 vW; void main(){ vec4 w = modelMatrix * instanceMatrix * vec4(position,1.0); vW = w.xyz; vN = normalize(mat3(modelMatrix * instanceMatrix) * normal); vV = normalize(cameraPosition - w.xyz); gl_Position = projectionMatrix * viewMatrix * w; }',
      fragmentShader: /* glsl */ `
        uniform float uTime, uOpacity; varying vec3 vN; varying vec3 vV; varying vec3 vW;
        void main(){
          float f = pow(1.0 - abs(dot(normalize(vN), vV)), 3.0);
          float fade = 1.0 - smoothstep(30.0, 110.0, length(cameraPosition - vW));
          float a = (0.006 + f * 0.09) * fade * uOpacity;
          gl_FragColor = vec4(vec3(0.6, 0.78, 1.0) * (0.01 + f * 0.22) * fade * uOpacity, a);
        }`,
    });
    const glass = new THREE.InstancedMesh(new THREE.BoxGeometry(CELL * 0.97, CELL * 0.97, CELL * 0.97), this.glassMat, cells.length);
    cells.forEach((c, i) => {
      dummy.position.copy(c);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      glass.setMatrixAt(i, dummy.matrix);
    });
    glass.renderOrder = 2;
    glass.frustumCulled = false;
    this.tesseract.add(glass);

    // Light fragments: thousands of small glowing tiles clinging to the steel, crowded at
    // the joints, some stretched along the beams. They flicker; bloom does the rest.
    const NF = 5200;
    this.fragments = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ toneMapped: false, transparent: true, opacity: 0 }), NF);
    this.fragBase = new Float32Array(NF);
    for (let i = 0; i < NF; i++) {
      const e = edges[Math.floor(Math.random() * edges.length)];
      const pos = e.c.clone();
      // Joints get most of them.
      const along = Math.random() < 0.6 ? (Math.random() < 0.5 ? -1 : 1) * (h - Math.random() * 0.9) : (Math.random() - 0.5) * CELL;
      pos.setComponent(e.axis, pos.getComponent(e.axis) + along);
      pos.add(new THREE.Vector3().randomDirection().multiplyScalar(0.12 + Math.random() * 0.35));
      dummy.position.copy(pos);
      dummy.rotation.set(0, 0, 0);
      const s = [0.03 + Math.random() * 0.14, 0.02 + Math.random() * 0.07, 0.03 + Math.random() * 0.14];
      s[e.axis] = Math.random() < 0.25 ? 0.4 + Math.random() * 1.6 : s[e.axis];
      dummy.scale.set(s[0], s[1], s[2]);
      dummy.updateMatrix();
      this.fragments.setMatrixAt(i, dummy.matrix);
      this.fragBase[i] = Math.random() < 0.3 ? 1.5 : 0.12 + Math.random() * 0.4;
      this.fragments.setColorAt(i, new THREE.Color(1, 1, 1));
    }
    this.fragments.frustumCulled = false;
    this.tesseract.add(this.fragments);
    this.tesseract.add(new THREE.AmbientLight(0x30343c, 0.25));
    for (let i = 0; i < 8; i++) {
      const l = new THREE.PointLight(i % 2 ? 0xcfe8ff : 0x9fd4ff, 5, 30, 1.6);
      l.position.set((i % 2 ? 1 : -1) * 4, (i % 3) - 1, -20 - i * 30);
      this.tesseract.add(l);
    }
    this.tesseract.visible = false;

    // The glass wall that shatters: a pane cut into irregular triangles.
    this.glassWall = new THREE.Group();
    // Faked glass (no transmission pass): environment reflections, a fresnel edge, light tint.
    const shardMat = new THREE.MeshPhysicalMaterial({ color: 0xcfe0ff, metalness: 0.1, roughness: 0.03, clearcoat: 1, clearcoatRoughness: 0.02, envMapIntensity: 2.2, transparent: true, opacity: 0.32, side: THREE.DoubleSide, depthWrite: false });
    const Wd = 16, Ht = 16, N = 7;
    const jitter = (x: number, y: number) => (Math.abs(x) >= Wd / 2 || Math.abs(y) >= Ht / 2 ? 0 : (Math.random() - 0.5) * 1.6);
    const pts: THREE.Vector2[][] = [];
    for (let i = 0; i <= N; i++) {
      pts.push([]);
      for (let jj = 0; jj <= N; jj++) {
        const x = -Wd / 2 + (Wd * i) / N, y = -Ht / 2 + (Ht * jj) / N;
        pts[i].push(new THREE.Vector2(x + jitter(x, y), y + jitter(x, y)));
      }
    }
    for (let i = 0; i < N; i++) for (let jj = 0; jj < N; jj++) {
      const a = pts[i][jj], b = pts[i + 1][jj], c = pts[i + 1][jj + 1], d = pts[i][jj + 1];
      for (const tri of [[a, b, c], [a, c, d]]) {
        const cen = tri[0].clone().add(tri[1]).add(tri[2]).divideScalar(3);
        const shape = new THREE.Shape(tri.map((v) => v.clone().sub(cen)));
        const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.08, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo, shardMat);
        mesh.position.set(cen.x, cen.y, 0);
        this.glassWall.add(mesh);
        this.shards.push({ mesh, vel: new THREE.Vector3(cen.x * 0.6, cen.y * 0.6, -6 - Math.random() * 10).add(new THREE.Vector3().randomDirection().multiplyScalar(3)), spin: new THREE.Vector3().randomDirection().multiplyScalar(4) });
      }
    }
    this.scene.add(this.glassWall);
  }

  private buildFinale(loader: GLTFLoader, base: string): void {
    // Relics from the realm orbit the astronaut: coins, a sword, a shield, a banner, a mug.
    const files = ['gear/gltf/sword_1handed.gltf', 'gear/gltf/shield_round.gltf', 'dungeon/gltf/coin_stack_small.gltf.glb', 'gear/gltf/mug_full.gltf', 'dungeon/gltf/banner_patternA_red.gltf.glb', 'gear/gltf/spellbook_open.gltf', 'dungeon/gltf/chest_gold.glb', 'gear/gltf/axe_1handed.gltf'];
    files.forEach((f, i) => {
      void loader.loadAsync(base + f).then((g) => {
        const o = g.scene;
        const box = new THREE.Box3().setFromObject(o);
        const s = 1.1 / Math.max(...box.getSize(new THREE.Vector3()).toArray());
        o.scale.setScalar(s);
        const holder = new THREE.Group();
        holder.add(o);
        holder.userData.phase = (i / files.length) * Math.PI * 2;
        holder.userData.tilt = (Math.random() - 0.5) * 0.9;
        holder.userData.r = 2.6 + (i % 3) * 0.7;
        this.finale.add(holder);
        this.relics.push(holder);
      });
    });
    this.portal = new THREE.Mesh(new THREE.TorusGeometry(5, 0.18, 32, 200), new THREE.MeshStandardMaterial({ color: 0xffb35c, emissive: 0xff7a2a, emissiveIntensity: 2.6, toneMapped: false }));
    this.finale.add(this.portal);
    this.finale.add(new THREE.PointLight(0xffa860, 30, 30, 1.5));
    this.finale.visible = false;
  }

  private buildHtml(): void {
    this.root.querySelectorAll<HTMLElement>('[data-a]').forEach((el) => this.sections.push({ el, a: Number(el.dataset.a), b: Number(el.dataset.b) }));
    this.splitLines = [...this.root.querySelectorAll<HTMLElement>('.st-split-line')].map((l) => [...l.querySelectorAll<HTMLElement>('span')]);
    this.revealLines = [...this.root.querySelectorAll<HTMLElement>('.st-rv-line span')];
    this.revealRule = this.root.querySelector<HTMLElement>('.st-rv-rule i');
    this.rail.innerHTML = this.sections.filter((s) => s.el.dataset.label).map((s) => `<button type="button" data-p="${(s.a + s.b) / 2}" aria-label="${s.el.dataset.label}"><span>${s.el.dataset.label}</span></button>`).join('');
    this.rail.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-p]');
      if (b) this.targetP = Number(b.dataset.p);
    });
    const form = this.root.querySelector<HTMLFormElement>('#studio-join')!;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.join(form);
    });
    try { if (localStorage.getItem('burnwood:ember')) this.markJoined(false); } catch { /* storage blocked: ask again */ }
    const gate = this.root.querySelector<HTMLElement>('#studio-gate')!;
    const closeGate = () => { gate.hidden = true; this.gateOpen = false; };
    gate.querySelector('#gate-sound')!.addEventListener('click', () => { void this.audio.resume(); closeGate(); });
    gate.querySelector('#gate-silent')!.addEventListener('click', () => { this.setMuted(true); closeGate(); });
    this.root.querySelector('#studio-mute')!.addEventListener('click', () => {
      this.setMuted(!this.audio.muted);
      if (!this.audio.muted) void this.audio.resume();
    });
  }

  private setMuted(m: boolean): void {
    this.audio.setMuted(m);
    const b = this.root.querySelector<HTMLButtonElement>('#studio-mute')!;
    b.setAttribute('aria-pressed', String(m));
    b.setAttribute('aria-label', m ? 'Turn sound on' : 'Turn sound off');
  }

  /** The signup: POST to /api/join, then the world answers. */
  private async join(form: HTMLFormElement): Promise<void> {
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const website = (form.elements.namedItem('website') as HTMLInputElement).value;
    const msg = this.root.querySelector<HTMLElement>('#studio-join-msg')!;
    const btn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      msg.textContent = 'That email doesn\u2019t look right. Check it and try again.';
      form.classList.add('bad');
      return;
    }
    form.classList.remove('bad');
    btn.disabled = true;
    btn.textContent = 'Lighting\u2026';
    try {
      const res = await fetch('/api/join', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, website, source: 'studio' }) });
      if (!res.ok) throw new Error(String(res.status));
      try { localStorage.setItem('burnwood:ember', '1'); } catch { /* fine */ }
      this.markJoined(true);
    } catch {
      msg.textContent = 'The forge didn\u2019t catch. Try again in a moment.';
      btn.disabled = false;
      btn.textContent = 'Light my ember';
    }
  }

  private markJoined(fresh: boolean): void {
    this.joined = true;
    const sec = this.root.querySelector<HTMLElement>('.st-join')!;
    sec.classList.add('joined');
    sec.querySelector('h2')!.textContent = fresh ? 'You\u2019re an Ember now.' : 'Welcome back, Ember.';
    sec.querySelector('.st-join-lead')!.textContent = 'You\u2019ll see Project 01 48 hours before anyone else.';
    this.root.querySelector('#studio-join-msg')!.textContent = '';
    if (fresh) {
      this.joinPulse = 1;
      this.audio.hit('confirm');
      this.audio.hit('portal');
    }
  }

  // ------------------------------------------------------------------ path

  /** Stepped travel through the tesseract: ease, hold, ease... one surge per scroll stretch. */
  private travel(p: number): number {
    const x = band(p, T.tesseractIn, T.shatter) * SURGES;
    return (Math.floor(x) + ease(x - Math.floor(x))) * CELL;
  }


  // ------------------------------------------------------------------ lifecycle

  async enter(): Promise<void> {
    await this.build();
    this.active = true;
    this.p = 0;
    this.targetP = 0;
    this.shattered = false;
    this.fell = false;
    for (const piece of this.corridorPieces) {
      piece.obj.position.copy(piece.obj.userData.home);
      piece.obj.rotation.copy(piece.obj.userData.rot);
      piece.vel.set(0, 0, 0);
      for (const m of piece.mats) { m.transparent = false; m.opacity = 1; }
    }
    for (const s of this.shards) {
      s.mesh.position.set(s.mesh.position.x, s.mesh.position.y, 0);
      s.mesh.rotation.set(0, 0, 0);
      s.mesh.userData.home ??= s.mesh.position.clone();
      s.mesh.position.copy(s.mesh.userData.home);
    }
    this.prevTone = { mapping: this.renderer.toneMapping, exposure: this.renderer.toneMappingExposure };
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.root.classList.add('open');
    this.resize();
    this.lastP = 0;
    this.ignitedHit = false;
    // Through the dungeon or the Skip link the visitor has already clicked, so sound can start.
    // A shared ?studio link arrives without a gesture: offer the one-click gate instead.
    this.audio.init();
    if (!this.audio.muted && !(await this.audio.resume())) {
      this.gateOpen = true;
      this.root.querySelector<HTMLElement>('#studio-gate')!.hidden = false;
    }
  }

  exit(): void {
    this.active = false;
    this.audio.stop();
    this.root.classList.remove('open');
    this.renderer.toneMapping = this.prevTone.mapping;
    this.renderer.toneMappingExposure = this.prevTone.exposure;
    this.renderer.setRenderTarget(null);
  }

  private bindInput(): void {
    const typing = () => (document.activeElement as HTMLElement | null)?.matches?.('input, textarea') ?? false;
    window.addEventListener('wheel', (e) => {
      if (this.active && !this.gateOpen && !typing()) this.targetP += e.deltaY * 0.00022;
    }, { passive: true });
    window.addEventListener('keydown', (e) => {
      if (this.active && !this.gateOpen && !(e.target as HTMLElement)?.matches?.('input, textarea')) this.keys.add(e.code);
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('pointermove', (e) => {
      if (this.active) this.lookTarget.set((e.clientX / innerWidth - 0.5) * 2, (e.clientY / innerHeight - 0.5) * 2);
    });
    window.addEventListener('touchstart', (e) => { if (this.active) this.touchY = e.touches[0].clientY; }, { passive: true });
    window.addEventListener('touchmove', (e) => {
      if (!this.active || this.gateOpen || this.touchY === null) return;
      const y = e.touches[0].clientY;
      this.targetP += (this.touchY - y) * 0.0006;
      this.touchY = y;
    }, { passive: true });
    window.addEventListener('touchend', () => (this.touchY = null));
    document.querySelector('#studio-back')!.addEventListener('click', () => this.hooks.onExit());
  }

  resize(): void {
    const w = this.renderer.domElement.clientWidth, h = this.renderer.domElement.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    const dpr = Math.min(window.devicePixelRatio || 1, 1.6);
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(w, h);
    this.bloom.setSize(w * dpr, h * dpr);
    this.halo.uniforms.uAspect.value = w / h;
  }

  // ------------------------------------------------------------------ frame

  update(dt: number, time: number): void {
    if (!this.active) return;
    if (this.keys.has('KeyW') || this.keys.has('ArrowDown') || this.keys.has('KeyS') === false && this.keys.has('Space')) this.targetP += dt * 0.05;
    if (this.keys.has('KeyS') || this.keys.has('ArrowUp')) this.targetP -= dt * 0.05;
    this.targetP = THREE.MathUtils.clamp(this.targetP, 0, 1);
    this.p += (this.targetP - this.p) * (1 - Math.exp(-dt * 2.4));
    const p = this.p;
    this.look.lerp(this.lookTarget, 1 - Math.exp(-dt * 2.5));

    this.updateCorridor(dt, time, p);
    this.animateAstronaut(dt, time, p);
    this.updateSpaceAndTesseract(dt, time, p);
    this.updateCamera(p, time);
    this.updateHtml(p);
    this.updateSound(dt, p);
    this.halo.uniforms.uTime.value = time;
    (this.stars.material as THREE.ShaderMaterial).uniforms.uTime.value = time;
  }

  private updateSound(dt: number, p: number): void {
    const speed = dt > 0 ? Math.abs(p - this.lastP) / dt : 0;
    if (this.lastP < T.portal && p >= T.portal) this.audio.hit('portal', 0.6);
    this.lastP = p;
    const music = (0.3 + 0.55 * band(p, T.floorBreak, T.hero) + 0.15 * band(p, T.reveal - 0.02, T.reveal + 0.04)) * (1 - band(p, 0.985, 1));
    const bright = Math.min(1, 0.06 + 0.45 * band(p, T.floorBreak, T.hero) + 0.5 * band(p, T.closeUp, T.reveal));
    const falling = p > T.floorBreak && p < T.closeUp ? 1 : 0.25;
    this.audio.mix(music, bright, THREE.MathUtils.clamp(speed * 7, 0, 1) * falling);
  }

  private updateCorridor(dt: number, time: number, p: number): void {
    const inCorridor = p < T.space + 0.02;
    this.corridor.visible = inCorridor;
    for (const t of this.torches) t.intensity = (t.userData.base ??= t.intensity) * (0.85 + Math.sin(time * 13 + t.position.z) * 0.08) * (1 - band(p, T.floorBreak, T.space));
    if (!this.fell && p > T.floorBreak) {
      this.fell = true;
      this.audio.hit('crack');
      this.audio.hit('boom', 0.7);
      // The floor gives way and the walls peel off into the dark.
      for (const piece of this.corridorPieces) {
        const out = piece.obj.position.clone();
        piece.vel.set(out.x * 2 + (Math.random() - 0.5) * 3, (piece.obj.userData.floor ? -1 : 1) * (2 + Math.random() * 6), (Math.random() - 0.5) * 4);
        piece.spin.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).multiplyScalar(3);
        for (const m of piece.mats) m.transparent = true;
      }
    }
    if (this.fell && inCorridor) {
      const k = band(p, T.floorBreak, T.space);
      for (const piece of this.corridorPieces) {
        piece.obj.position.addScaledVector(piece.vel, dt);
        piece.obj.rotation.x += piece.spin.x * dt;
        piece.obj.rotation.y += piece.spin.y * dt;
        for (const m of piece.mats) m.opacity = 1 - k;
      }
    }
    if (p < T.floorBreak - 0.01 && this.fell) {
      // Scrolled back up: rebuild the hall.
      this.fell = false;
      for (const piece of this.corridorPieces) {
        piece.obj.position.copy(piece.obj.userData.home);
        piece.obj.rotation.copy(piece.obj.userData.rot);
        for (const m of piece.mats) { m.opacity = 1; m.transparent = false; }
      }
    }
  }

  private updateSpaceAndTesseract(dt: number, time: number, p: number): void {
    const spaceK = band(p, T.floorBreak, T.space + 0.03);
    (this.stars.material as THREE.ShaderMaterial).uniforms.uOpacity.value = spaceK;
    this.space.visible = spaceK > 0;
    this.halo.uniforms.uHalo.value = spaceK * (1 - band(p, T.join, T.portal) * 0.6);

    const tunnelK = band(p, T.tesseractIn, T.shatter);
    // Tesseract: dust lines draw the lattice in around him, steel fills it, then it surges.
    const dustK = band(p, T.tesseractIn - 0.06, T.tesseractIn - 0.02);
    const steelK = ease(band(p, T.tesseractIn - 0.025, T.tesseractIn + 0.02));
    this.tesseract.visible = dustK > 0.001 && p < T.join + 0.04;
    (this.dust.material as THREE.PointsMaterial).opacity = dustK * (1 - steelK * 0.75);
    this.beamMat.opacity = steelK;
    this.beamMat.transparent = steelK < 0.999;
    this.glassMat.uniforms.uOpacity.value = steelK;
    this.glassMat.uniforms.uTime.value = time;
    (this.fragments.material as THREE.MeshBasicMaterial).opacity = ease(band(p, T.tesseractIn - 0.01, T.tesseractIn + 0.04));
    const z = this.travel(p) + time * 0.6 * tunnelK * (1 - band(p, T.shatter - 0.04, T.shatter));
    // Surging speed drives the ghost trails (the afterimage pass).
    const speed = dt > 0 ? Math.abs(z - this.tesseract.position.z) / dt : 0;
    this.tesseract.position.z = z;
    this.trailDamp += (THREE.MathUtils.clamp(speed / 22, 0, 1) * 0.86 * (tunnelK > 0 && tunnelK < 1 ? 1 : 0) - this.trailDamp) * (1 - Math.exp(-dt * 6));
    const spinBlur = p > T.hero && p < T.closeUp ? THREE.MathUtils.clamp((this.spin.length() - 0.6) * 0.25, 0, 0.55) : 0;
    const damp = Math.max(this.trailDamp, spinBlur);
    this.afterimage.uniforms.damp.value = damp;
    this.afterimage.enabled = damp > 0.02;
    // Light palettes change the deeper you go: white-ice, then teal, then ember-red and teal.
    const stage = Math.min(2, Math.floor(tunnelK * 3));
    const palettes = [[0xffffff, 0xcff4ff, 0x7fe8ff, 0xe8f0ff], [0x7fffe0, 0x3fd8ff, 0xffffff, 0x9ffff0], [0xff4a3a, 0x3fffd8, 0xff8a6a, 0xffffff]];
    const col = new THREE.Color();
    if (stage !== this.paletteStage) {
      this.paletteStage = stage;
      for (let i = 0; i < this.fragBase.length; i++) this.fragments.setColorAt(i, col.setHex(palettes[stage][i % 4]).multiplyScalar(this.fragBase[i]));
    }
    // Flicker: re-light a handful of tiles every frame.
    for (let n = 0; n < 160; n++) {
      const i = Math.floor(Math.random() * this.fragBase.length);
      const on = Math.random() < 0.5 ? 1.7 : this.fragBase[i] * 0.3;
      this.fragments.setColorAt(i, col.setHex(palettes[stage][i % 4]).multiplyScalar(on));
    }
    if (this.fragments.instanceColor) this.fragments.instanceColor.needsUpdate = true;
    // Burnwood's world sits low in the distance behind him for the hello.
    this.planet.visible = false; // he wants pure space: no planet
    this.planet.rotation.y += dt * 0.01;

    // The glass wall rushes up the tunnel and breaks on the astronaut.
    const approach = band(p, T.shatter - 0.12, T.shatter);
    this.glassWall.visible = p > T.shatter - 0.12 && p < T.join + 0.04;
    this.glassWall.position.set(0, 0, THREE.MathUtils.lerp(-150, this.astroBase.z - 1.5, ease(approach)));
    if (!this.shattered && p > T.shatter) {
      this.shattered = true;
      this.audio.hit('glass');
      this.audio.hit('boom', 0.8);
      for (const sh of this.shards) sh.mesh.userData.home ??= sh.mesh.position.clone();
    }
    if (this.shattered && p < T.shatter - 0.01) {
      this.shattered = false;
      for (const sh of this.shards) { sh.mesh.position.copy(sh.mesh.userData.home); sh.mesh.rotation.set(0, 0, 0); }
    }
    if (this.shattered) {
      for (const sh of this.shards) {
        sh.mesh.position.x += sh.vel.x * dt * 0.7;
        sh.mesh.position.y += sh.vel.y * dt * 0.7;
        sh.mesh.position.z += Math.abs(sh.vel.z) * dt * 0.9; // toward the camera
        sh.mesh.rotation.x += sh.spin.x * dt;
        sh.mesh.rotation.y += sh.spin.y * dt;
      }
    }
    // Bloom overdrive at the break reads as a white flash.
    const flash = Math.max(0, 1 - Math.abs(p - T.shatter) / 0.014);
    this.bloom.strength = 0.55 + flash * 6;
    // Keep the white suit crisp: only real light sources bloom while he's close.
    this.bloom.threshold = p < T.tesseractIn - 0.03 ? 1.15 : 0.95;
    // Dungeon: torchlight only. Space and the tesseract: soft reflections.
    this.scene.environmentIntensity = band(p, T.floorBreak, T.space) * 0.35;
    this.scene.fog = p > T.tesseractIn - 0.06 && p < T.join ? this.tunnelFog : null;
    this.halo.uniforms.uFlash.value = flash * 0.55;

    // Finale: relics orbit him, he waves; the portal waits ahead.
    const joinK = band(p, T.join - 0.02, T.join + 0.04);
    this.finale.visible = joinK > 0;
    this.finale.position.copy(this.astronaut.position);
    this.relics.forEach((r) => {
      const a = time * 0.35 + r.userData.phase;
      const rad = r.userData.r * (0.4 + 0.6 * joinK);
      r.position.set(Math.cos(a) * rad, Math.sin(a * 1.3) * 0.8 + r.userData.tilt * Math.sin(a) * 2, Math.sin(a) * rad);
      r.rotation.x += dt * 0.6;
      r.rotation.y += dt * 0.8;
      r.scale.setScalar(joinK);
    });
    this.portal.position.set(0, 0.6, -10 - (1 - band(p, T.portal - 0.06, T.portal)) * 40);
    this.portal.rotation.z = time * 0.2;
    // Dormant until you join; signing up ignites it.
    this.portalGlow += ((this.joined ? 1 : 0) - this.portalGlow) * (1 - Math.exp(-dt * 1.6));
    this.joinPulse = Math.max(0, this.joinPulse - dt * 0.9);
    const pm = this.portal.material as THREE.MeshStandardMaterial;
    pm.emissiveIntensity = 0.35 + this.portalGlow * 2.25 + this.joinPulse * 3;
    pm.color.setRGB(0.35 + this.portalGlow * 0.65, 0.3 + this.portalGlow * 0.4, 0.28 + this.portalGlow * 0.08);
    this.bloom.strength += this.joinPulse * 2.5;
    this.halo.uniforms.uFlash.value += this.joinPulse * this.joinPulse * 0.35;
    this.portal.scale.setScalar(0.5 + band(p, T.join, T.portal) * 0.6);
    this.halo.uniforms.uFade.value = band(p, 0.993, 1.0);
  }

  /** Where he is, which way he faces, and what his body is doing, all from p and time. */
  private animateAstronaut(dt: number, time: number, p: number): void {
    const t = time;
    const emerge = ease(band(p, T.floorBreak, T.hero - 0.035));
    const fallK = band(p, T.hero, T.fallEnd);
    const upright = ease(band(p, T.fallEnd, T.closeUp + 0.01));
    const face = ease(band(p, T.join - 0.03, T.join + 0.02));
    const away = ease(band(p, T.tesseractIn - 0.05, T.tesseractIn + 0.01)) * (1 - face);
    const still = upright * (1 - away);

    // Position: drops past the camera as the floor goes, then a slow zero-g drift.
    const amp = 0.35 * (1 - still * 0.8) * (1 - away * 0.6);
    const pos = this.astronaut.position.copy(this.astroBase).add(new THREE.Vector3(
      Math.sin(t * 0.31) * 0.7 + Math.sin(t * 0.73 + 1.3) * 0.3,
      Math.sin(t * 0.27 + 2.1) * 0.5 + Math.sin(t * 0.61) * 0.2,
      Math.sin(t * 0.19 + 0.7) * 0.6,
    ).multiplyScalar(amp));
    // Out of the dark toward you, then away down the depth, accelerating; the camera
    // catches back up (and he's drawn back home) during the dive to the visor.
    pos.z += (-3 - Math.pow(fallK, 1.6) * 62) * (1 - upright) - (1 - emerge) * 26;
    pos.y += (1 - emerge) * -1.5 - Math.pow(fallK, 1.6) * 4 * (1 - upright);
    this.astronaut.visible = p > T.floorBreak;
    this.astroLights.position.copy(pos);

    // Orientation: a wild tumble that decays into a belly-down skydive, then he rights
    // himself to face you, turns into the tunnel, and turns back for the finale.
    // He tips over backwards and tumbles slowly, like he's sinking through water.
    const g = ease(fallK);
    const fall = new THREE.Vector3(
      -g * 2.7 + Math.sin(t * 0.5) * 0.06 * (1 - g) - fallK * Math.sin(t * 0.21) * 0.3,
      Math.sin(g * 3.1) * 0.55 + Math.sin(t * 0.27) * 0.12 * g,
      g * 1.5 + Math.sin(t * 0.23) * 0.2 * g + Math.sin(t * 0.6) * 0.03,
    );
    const pullK = ease(band(p, T.pullBack, T.reveal));
    const hello = new THREE.Vector3(0.04 + Math.sin(t * 0.5) * 0.03, -0.24 * pullK + Math.sin(t * 0.37) * 0.05, Math.sin(t * 0.43) * 0.04);
    const tunnel = new THREE.Vector3(-1.9 + Math.sin(t * 0.19) * 1.1, Math.PI * 0.8 + Math.sin(t * 0.13) * 0.9, 0.6 + Math.sin(t * 0.16 + 1) * 1.2);
    const r = fall.lerp(hello, upright).lerp(tunnel, away);
    // Momentum: the body chases its target orientation through a soft spring, so tumbles
    // carry weight instead of snapping to the scroll.
    if (!this.rootInit) { this.rootSpring.x.copy(r); this.rootInit = true; }
    springTo(this.rootSpring, r, 9, 0.75, dt);
    const rs = this.rootSpring.x;
    this.astronaut.rotation.set(rs.x, rs.y, rs.z);
    // Angular velocity in body space (for limb drag and motion blur).
    const qNow = this.astronaut.quaternion;
    const dq = this.prevRoot.clone().invert().multiply(qNow);
    const ang = 2 * Math.acos(THREE.MathUtils.clamp(Math.abs(dq.w), 0, 1));
    const axis = new THREE.Vector3(dq.x, dq.y, dq.z);
    if (axis.lengthSq() > 1e-10 && dt > 0) axis.normalize().multiplyScalar((Math.sign(dq.w) || 1) * ang / dt);
    else axis.set(0, 0, 0);
    this.spin.lerp(axis.clampLength(0, 8), 1 - Math.exp(-dt * 10));
    this.prevRoot.copy(qNow);

    // Body: hero hold -> loose drift while falling -> his own idle float -> his own wave.
    const k2 = ease(band(fallK, 0.0, 0.45));
    const hero = (1 - k2) * (1 - upright);
    const waveOn = Math.max(
      ease(band(p, T.pullBack + 0.012, T.pullBack + 0.05)) * (1 - ease(band(p, T.tesseractIn - 0.07, T.tesseractIn - 0.03))),
      ease(band(p, T.join - 0.005, T.join + 0.03)),
    ) * upright;
    const fd = k2 * (1 - upright) + (upright - waveOn) * away; // drifting: the fall, and the tunnel
    this.idle.setEffectiveWeight(1 - waveOn);
    this.wave.setEffectiveWeight(waveOn);
    this.mixer.update(dt);
    // Organic motion: smooth noise (each limb its own seed) instead of looping sines.
    const N = (seed: number, rate: number) => fbm(t * rate * 0.55 + seed * 13.17);
    // Inertia: limbs trail the spin. Pitching (x) drags arms and legs, rolling (z) splays them.
    const wx = THREE.MathUtils.clamp(this.spin.x, -4, 4), wz = THREE.MathUtils.clamp(this.spin.z, -4, 4);
    const drag = (hero + k2 * (1 - upright) + upright * away) * 7;
    this.pose([[HERO, hero], [DRIFT, fd]], {
      Torso: [N(1, 0.9) * 7 * fd + N(2, 0.6) * 2.5 * hero - wx * drag * 0.4, N(3, 0.7) * 6 * fd, N(4, 0.6) * 5 * fd],
      Chest: [N(5, 0.8) * 3 * (fd + hero), 0, 0],
      Head: [N(6, 0.8) * 9 * fd + wx * drag * 0.6, N(7, 0.6) * 12 * fd + N(8, 0.45) * 5 * hero, N(9, 0.5) * 4 * (fd + hero)],
      UpperArmL: [N(10, 1.2) * 15 * fd + N(11, 0.7) * 5 * hero + wx * drag * 2.2 + wz * drag * 1.4, N(12, 1.0) * 6 * fd, N(13, 0.9) * 10 * fd],
      UpperArmR: [N(14, 1.1) * 15 * fd + N(15, 0.7) * 5 * hero + wx * drag * 2.2 - wz * drag * 1.4, N(16, 1.0) * 6 * fd, N(17, 1.1) * 10 * fd],
      LowerArmL: [0, 0, -(N(18, 1.4) * 18 * fd + N(19, 0.9) * 7 * hero) + wx * drag * 1.2],
      LowerArmR: [0, 0, N(20, 1.3) * 18 * fd + N(21, 0.9) * 7 * hero - wx * drag * 1.2],
      UpperLegL: [N(22, 0.8) * 13 * fd + N(23, 0.5) * 4 * hero - wx * drag * 1.6, 0, wz * drag * 0.6],
      UpperLegR: [N(24, 0.8) * 13 * fd + N(25, 0.5) * 4 * hero - wx * drag * 1.6, 0, -wz * drag * 0.6],
      LowerLegL: [Math.abs(N(26, 1.0)) * 18 * fd + N(27, 0.7) * 5 * hero + Math.abs(wx) * drag * 1.2, 0, 0],
      LowerLegR: [Math.abs(N(28, 1.0)) * 18 * fd + N(29, 0.7) * 5 * hero + Math.abs(wx) * drag * 1.2, 0, 0],
    }, hero + fd, dt);

    // The little game on his visor.
    const screen = ease(band(p, T.closeUp - 0.012, T.closeUp + 0.015)) * (1 - band(p, T.tesseractIn - 0.05, T.tesseractIn - 0.01));
    (this.visorScreen.material as THREE.MeshBasicMaterial).opacity = screen;
    this.visorScreen.visible = screen > 0.001;
    // Mount Cinder erupts on the visor and the line burns in; its light floods the helmet.
    const ignite = band(p, T.closeUp + 0.012, T.pullBack - 0.004);
    if (this.visorScreen.visible) this.visorGame.update(Math.min(dt, 0.05), ignite);
    (this.visorScreen.material as THREE.MeshBasicMaterial).color.setScalar(1.5 + ignite * 0.35);
    this.visorLight.intensity = ease(ignite) * 0.18 * screen;
    if (!this.ignitedHit && ignite > 0.25) { this.ignitedHit = true; this.audio.hit('ignite'); }
    if (p < T.closeUp - 0.01) this.ignitedHit = false;

    // He looks at you: head and neck follow the pointer in the calm shots, and find you
    // when you sign up. Layered on top of whatever the clips and poses just wrote.
    const calm = band(p, T.pullBack, T.pullBack + 0.03) * (1 - band(p, T.tesseractIn - 0.07, T.tesseractIn - 0.04)) + face;
    const gw = Math.min(1, calm) * (1 - this.joinPulse);
    const gazeTarget = new THREE.Vector3(this.look.y * 14 * gw, this.look.x * 24 * gw, 0);
    springTo(this.gaze, gazeTarget, 40, 0.8, dt);
    const d2r = THREE.MathUtils.DEG2RAD;
    const gq = new THREE.Quaternion();
    for (const [name, share] of [['Neck', 0.3], ['Head', 0.7]] as const) {
      const b = this.bones[name]?.bone;
      if (b) b.quaternion.multiply(gq.setFromEuler(new THREE.Euler(this.gaze.x.x * share * d2r, this.gaze.x.y * share * d2r, 0)));
    }

  }

  private updateCamera(p: number, time: number): void {
    if (p < T.floorBreak + 0.005) {
      // First person in the corridor, creeping forward.
      const z = 1 - band(p, 0, T.floorBreak) * 8;
      this.camera.position.set(this.look.x * 0.3, 1.7 + Math.sin(z * 2.4) * 0.03, z);
      this.camera.up.set(0, 1, 0);
      this.camera.rotation.set(-this.look.y * 0.12, -this.look.x * 0.18, 0, 'YXZ');
      this.camera.fov = 62;
      this.camera.updateProjectionMatrix();
      return;
    }
    // Keyframed shots, each relative to his body or to the visor (a = 1).
    const body = this.astronaut.position;
    const visor = this.visorScreen.getWorldPosition(new THREE.Vector3());
    let i = 0;
    while (i < CAM_KEYS.length - 2 && p > CAM_KEYS[i + 1].p) i++;
    const k0 = CAM_KEYS[i], k1 = CAM_KEYS[i + 1];
    const u = ease(THREE.MathUtils.clamp((p - k0.p) / (k1.p - k0.p), 0, 1));
    const anchor = (k: CamKey) => body.clone().lerp(visor, k.a).multiplyScalar(1 - (k.abs ?? 0));
    // Visor shots are framed in the visor's own frame (yaw only), so the camera always looks into it.
    const vq = this.visorScreen.getWorldQuaternion(new THREE.Quaternion());
    const fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(vq).setY(0);
    const yaw = fwd.lengthSq() > 1e-6 ? Math.atan2(fwd.x, fwd.z) : 0;
    const off = (v: THREE.Vector3, a: number) => v.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw * a);
    const pos = anchor(k0).add(off(k0.pos, k0.a)).lerp(anchor(k1).add(off(k1.pos, k1.a)), u);
    const target = anchor(k0).add(off(k0.look, k0.a)).lerp(anchor(k1).add(off(k1.look, k1.a)), u);
    const close = THREE.MathUtils.lerp(k0.a, k1.a, u);
    const drift = 1 - close;
    pos.x += (Math.sin(time * 0.11) * 0.25 + this.look.x * 0.5) * drift;
    pos.y += (Math.sin(time * 0.09) * 0.15 - this.look.y * 0.35) * drift;
    // Leaving the corridor: ease out of the first-person spot.
    const leave = ease(band(p, T.floorBreak, T.floorBreak + 0.035));
    pos.lerp(new THREE.Vector3(0, 1.7, -7), 1 - leave);
    target.lerp(new THREE.Vector3(0, 0.6, -14), 1 - leave);
    // Through the portal at the very end.
    const through = ease(band(p, T.portal + 0.02, 1));
    pos.lerp(this.portal.position.clone().add(new THREE.Vector3(0, 0, 1.5)), through);
    target.lerp(this.portal.position, through);
    this.camera.position.copy(pos);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(target);
    const tunnelK = band(p, T.tesseractIn, T.shatter);
    this.camera.fov = THREE.MathUtils.lerp(k0.fov, k1.fov, u) + Math.sin(tunnelK * 10 + time * 0.8) * 5 * Math.sin(tunnelK * Math.PI) + band(p, T.shatter - 0.015, T.shatter) * (1 - band(p, T.shatter, T.join)) * 16;
    this.camera.updateProjectionMatrix();
  }

  private updateHtml(p: number): void {
    let current = -1;
    let li = 0;
    this.sections.forEach((s) => {
      const fadeIn = band(p, s.a - 0.015, s.a + 0.01);
      const fadeOut = 1 - band(p, s.b - 0.01, s.b + 0.015);
      const vis = Math.min(fadeIn, fadeOut);
      s.el.style.opacity = vis.toFixed(3);
      s.el.style.transform = `translateY(${((1 - vis) * 26).toFixed(1)}px)`;
      s.el.classList.toggle('live', vis > 0.5);
      if (s.el.dataset.label) {
        if (vis > 0.5) current = li;
        li++;
      }
    });
    // As he falls away the words drift apart around him, then the block swells past the camera.
    const sk = band(p, T.hero - 0.018, T.fallEnd - 0.01);
    const spread = ease(band(sk, 0.25, 0.7));
    const swell = Math.pow(band(sk, 0.5, 1), 2.4);
    const wrap = this.root.querySelector<HTMLElement>('.st-split');
    if (wrap) {
      wrap.style.transform = `translate(-50%, -50%) scale(${(1 + swell * 9).toFixed(3)})`;
      wrap.style.opacity = Math.min(band(sk, 0, 0.1), 1 - band(sk, 0.82, 1)).toFixed(3);
    }
    this.splitLines.forEach((words) => words.forEach((el, i) => {
      el.style.transform = `translateX(${((i - (words.length - 1) / 2) * spread * 9).toFixed(2)}vw)`;
    }));
    // The hello: lines rise out of their masks one after another, then lift away.
    const k = band(p, T.reveal - 0.004, T.reveal + 0.05);
    const out = ease(band(p, 0.452, 0.474));
    this.revealLines.forEach((el, i) => {
      const inK = ease(band(k, i * 0.14, i * 0.14 + 0.5));
      el.style.transform = `translateY(${((1 - inK) * 110 - out * 110).toFixed(1)}%)`;
    });
    if (this.revealRule) this.revealRule.style.transform = `scaleX(${(ease(band(k, 0.55, 1)) * (1 - out)).toFixed(3)})`;
    this.rail.classList.toggle('on', p > T.space);
    this.rail.querySelectorAll('button').forEach((b, i) => b.classList.toggle('now', i === current));
    document.querySelector('#studio-hint')!.classList.toggle('on', p < T.hero);
    const cd = document.querySelector('#studio-count');
    if (cd) {
      let s = Math.max(0, Math.floor((new Date(2027, 0, 1).getTime() - Date.now()) / 1000));
      const d = Math.floor(s / 86400); s %= 86400;
      const h = Math.floor(s / 3600); s %= 3600;
      const m = Math.floor(s / 60);
      const text = `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s % 60).padStart(2, '0')}s`;
      if (cd.textContent !== text) cd.textContent = text;
    }
  }

  render(): void {
    if (this.active) this.composer.render();
  }
}
